/**
 * Poptávkový formulář.
 *
 *  - kontrola povinných polí s hláškou u pole i souhrnem nahoře (WCAG 3.3.1)
 *  - u pole s fotkami ukáže, kolik souborů je vybraných
 *  - odeslání přes fetch na /api/lead/submit (ContactController v cms.local);
 *    když server neodpoví, formulář řekne, ať návštěvník zavolá – poptávka
 *    se nesmí ztratit potichu
 *  - během odeslání překryje formulář vrstva se spinnerem; po úspěchu ukáže
 *    hlášku, formulář vymaže a token reCAPTCHY zahodí – jde poslat další
 *  - VŠECHNY <select> na webu jsou Choices.js (stejně jako kominik.local).
 *    Opt-out: atribut data-no-choices.
 */
import { loadVendor, loadStyle } from './lazy-vendor.js';

const CHOICES = '/www/assets/js/vendor/choices/choices.min.js';

/**
 * Knihovna (~21 kB gzip) se stáhne až ve chvíli, kdy se k selectu doroluje –
 * úvod stránky ji neplatí a do té doby funguje nativní <select>.
 */
/** Instance Choices podle <select> – reset formuláře je musí vrátit ručně. */
const choicesBySelect = new WeakMap();

async function initChoices() {
    const selects = [...document.querySelectorAll('select')]
        .filter((s) => !s.hasAttribute('data-no-choices') && !s.closest('.choices'));
    if (!selects.length) return;

    const [ok] = await Promise.all([loadVendor(CHOICES), loadStyle('choices')]);
    if (!ok) return;   // nedorazila → nativní select zůstane
    if (typeof Choices === 'undefined') return;

    selects.forEach((select) => {
        const initial = select.value; // na detailu služby je předvybraná
        const instance = new Choices(select, {
            searchEnabled: select.options.length > 8, // hledání jen u delších seznamů
            shouldSort: false,                         // zachovat pořadí <option>
            allowHTML: false,
            itemSelectText: '',
            loadingText: 'Načítání…',
            noResultsText: 'Žádné výsledky',
            noChoicesText: 'Žádné možnosti',
        });

        // Choices obalí select divem s role="listbox" a původní <select> nechá
        // uvnitř jako nosič hodnoty – pro odečítače rozbitý listbox (axe:
        // aria-required-children). Přístupné UI staví Choices sám, původní
        // select schováme; hodnotu formulář odešle dál. Shodné s kominik.local.
        select.setAttribute('aria-hidden', 'true');
        select.setAttribute('tabindex', '-1');
        choicesBySelect.set(select, { instance, initial });
    });
}

function initChoicesLazy() {
    const selects = document.querySelectorAll('select:not([data-no-choices])');
    if (!selects.length) return;

    if (!('IntersectionObserver' in window)) { initChoices(); return; }

    const io = new IntersectionObserver((entries, obs) => {
        if (!entries.some((e) => e.isIntersecting)) return;
        obs.disconnect();
        initChoices();
    }, { rootMargin: '400px' });

    selects.forEach((s) => io.observe(s));
}
function fieldBox(input) {
    return input.closest('.form-field');
}

function setError(input, message) {
    const box = fieldBox(input);
    const err = box && box.querySelector('.form-error');
    if (!box || !err) return;
    box.classList.toggle('is-invalid', Boolean(message));
    input.setAttribute('aria-invalid', message ? 'true' : 'false');
    err.textContent = message || '';
}

function validate(form) {
    const invalid = [];
    form.querySelectorAll('[required]').forEach((input) => {
        const empty = !String(input.value || '').trim();
        const msg = empty ? fieldBox(input).querySelector('.form-error').dataset.message : '';
        setError(input, msg);
        if (msg) invalid.push(input);
    });
    return invalid;
}

function initFileLabel(form) {
    form.querySelectorAll('.file-input').forEach((input) => {
        const text = input.closest('.file-drop').querySelector('.file-drop-text');
        input.addEventListener('change', () => {
            const n = input.files.length;
            text.textContent = n === 0 ? text.dataset.empty
                : n === 1 ? input.files[0].name
                : `Vybráno ${n} ${n < 5 ? 'fotky' : 'fotek'}`;
        });
    });
}

/**
 * Vrátí formulář do výchozího stavu, aby šlo hned poslat další poptávku:
 * hodnoty, Choices, popisek u fotek, chybové stavy i token reCAPTCHY
 * (v3 vydá nový token při dalším odeslání, starý by server odmítl).
 */
function resetForm(form) {
    form.reset();
    form.querySelectorAll('select').forEach((select) => {
        const c = choicesBySelect.get(select);
        if (!c) return;
        c.instance.removeActiveItems();
        c.instance.setChoiceByValue(c.initial);
    });
    form.querySelectorAll('.file-input').forEach((input) => input.dispatchEvent(new Event('change')));
    form.querySelectorAll('[aria-invalid]').forEach((input) => setError(input, ''));

    const token = form.querySelector('input[name="g-recaptcha-response"]');
    if (token) token.value = '';
    // reCAPTCHA v2 (checkbox) by se musela resetovat i vizuálně
    if (window.grecaptcha && typeof window.grecaptcha.reset === 'function' && form.querySelector('.g-recaptcha')) {
        try { window.grecaptcha.reset(); } catch { /* v3 widget nemá */ }
    }
}

/** Vrstva se spinnerem přes formulář během odesílání. */
function setBusy(form, busy) {
    let overlay = form.querySelector('.form-overlay');
    if (!overlay) {
        overlay = document.createElement('div');
        overlay.className = 'form-overlay';
        overlay.hidden = true;
        overlay.innerHTML = '<span class="form-spinner" aria-hidden="true"></span>'
            + '<span class="form-overlay-text">Odesíláme poptávku…</span>';
        form.appendChild(overlay);
    }
    overlay.hidden = !busy;
    form.classList.toggle('is-busy', busy);
    form.setAttribute('aria-busy', busy ? 'true' : 'false');
}

function initForm(form) {
    const summary = form.querySelector('.form-summary');
    const success = form.querySelector('.form-success');
    const submit  = form.querySelector('[type="submit"]');

    initFileLabel(form);

    // chybu u pole smažeme hned, jak ji návštěvník opraví
    form.addEventListener('input', (e) => {
        if (e.target.matches('[required]') && String(e.target.value).trim()) setError(e.target, '');
    });

    form.addEventListener('submit', async (e) => {
        e.preventDefault();
        if (form.classList.contains('is-busy')) return; // dvojklik
        summary.hidden = true;
        success.hidden = true;

        const invalid = validate(form);
        if (invalid.length) {
            summary.textContent = invalid.length === 1
                ? 'Formulář nejde odeslat, zkontrolujte prosím označené pole.'
                : `Formulář nejde odeslat, zkontrolujte prosím označená pole (${invalid.length}).`;
            summary.hidden = false;
            invalid[0].focus();
            return;
        }

        submit.disabled = true;
        setBusy(form, true);

        // Token reCAPTCHY platí jen dvě minuty, proto se bere až teď.
        await fillRecaptchaToken(form);

        const failText = 'Poptávku se nepodařilo odeslat. Zkuste to prosím znovu, nebo nám rovnou zavolejte'
            + (form.dataset.phone ? ' na ' + form.dataset.phone : '') + '.';
        try {
            const res = await fetch(form.action, {
                method: 'POST',
                body: new FormData(form),
                headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
            });
            // ContactController z CMS vrací {ok} nebo {ok: false, error: „konkrétní důvod"}
            let data = {};
            try { data = await res.json(); } catch { /* prázdná / ne-JSON odpověď */ }
            if (!res.ok || !data.ok) {
                const err = new Error(String(res.status));
                err.userMessage = data.error;
                throw err;
            }

            resetForm(form);
            success.hidden = false;
            // hláška je nad poli – posunout k ní, ať ji návštěvník uvidí
            success.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } catch (err) {
            summary.textContent = err && err.userMessage ? err.userMessage : failText;
            summary.hidden = false;
            summary.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        } finally {
            setBusy(form, false);
            submit.disabled = false;
        }
    });

    // Po úspěchu hláška zmizí, jakmile návštěvník začne psát další poptávku.
    form.addEventListener('input', () => { success.hidden = true; });

    // Skript reCAPTCHY (~450 kB) se stáhne, až s formulářem někdo začne
    // pracovat – na načtení stránky by brzdil PageSpeed. Stejně jako cms.local.
    if (form.dataset.recaptchaKey) {
        const warm = () => loadRecaptcha(form.dataset.recaptchaKey).catch(() => {});
        ['pointerover', 'focusin', 'touchstart'].forEach((ev) => form.addEventListener(ev, warm, { once: true, passive: true }));
    }
}

/**
 * Doplní token reCAPTCHY v3 do skrytého pole (jen když je ochrana v CMS
 * zapnutá). Selhání odeslání nezastaví – server si token ověřuje sám a je
 * lepší poslat poptávku bez tokenu než ji kvůli výpadku cizího skriptu ztratit.
 */
async function fillRecaptchaToken(form) {
    const key = form.dataset.recaptchaKey;
    const field = form.querySelector('input[name="g-recaptcha-response"]');
    if (!key || !field) return;
    try {
        await loadRecaptcha(key);
        await new Promise((resolve) => window.grecaptcha.ready(resolve));
        field.value = await window.grecaptcha.execute(key, { action: form.dataset.recaptchaAction || 'submit' });
    } catch (err) {
        console.warn('[reCAPTCHA] token se nepodařilo získat', err);
    }
}

let recaptchaLoading = null;

/** Načte api.js reCAPTCHY jednou za stránku; po 10 s to vzdá. */
function loadRecaptcha(key) {
    if (window.grecaptcha) return Promise.resolve();
    if (recaptchaLoading) return recaptchaLoading;
    recaptchaLoading = new Promise((resolve, reject) => {
        const s = document.createElement('script');
        s.src = 'https://www.google.com/recaptcha/api.js?render=' + encodeURIComponent(key);
        s.async = true;
        const timer = setTimeout(() => reject(new Error('timeout')), 10000);
        s.onload = () => { clearTimeout(timer); resolve(); };
        s.onerror = () => { clearTimeout(timer); reject(new Error('load failed')); };
        document.head.appendChild(s);
    });
    recaptchaLoading.catch(() => { recaptchaLoading = null; });
    return recaptchaLoading;
}

document.querySelectorAll('form.js-inquiry').forEach(initForm);
initChoicesLazy();
